import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Grid, Column, Tile, Tag, InlineNotification, Modal,
  TextArea, Dropdown, RadioButtonGroup, RadioButton, Toggle,
  TextInput, NumberInput, Select, SelectItem, Checkbox,
} from '@carbon/react';
import {
  ArrowLeft, Download, Copy, Checkmark, WarningFilled, Location, Time, Edit,
  Camera, QrCode, Search,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useZones } from '../hooks/useZones';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useGatewaysOptimized } from '../hooks/useGatewaysOptimized';
import { supabase } from '../services/supabaseClient';
import {
  getShipment, getShipmentByEpc, getShipmentsByIds, updateShipmentStatus, setShipmentQaNotes, confirmHandoff, listHandoffs,
  checkHandoffAuthorization,
  updateShipmentMetadata,
  listShipments,
  listShipmentAggregations,
  listActivePackedChildIds,
  packShipmentsIntoParent,
  unpackShipmentsFromParent,
} from '../services/shipmentService';
import {
  fetchShipmentTelemetry,
  getActiveShipmentGatewayBinding,
  listShipmentGatewayBindings,
} from '../services/shipmentTelemetryService';
import ShipmentLabel from '../components/ShipmentLabel';
import QRCodeScanner from '../components/QRCodeScanner';
import { formatLgtinUrn, toGs1Uom, applyGtinInputNormalization, applySsccInputNormalization, normalizeGtin14, normalizeSscc18 } from '../utils/gs1Utils';

const STATUS_TAG_TYPE = {
  commissioned: 'blue',
  in_transit: 'purple',
  delivered: 'green',
  quarantined: 'red',
};

const STATUS_LABEL = {
  commissioned: 'Commissioned',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  quarantined: 'Quarantined',
};

const BIZSTEP_LABEL = {
  commissioning: 'Commissioning',
  shipping: 'Shipping',
  in_transit: 'In Transit',
  receiving: 'Receiving',
  packing: 'Packing (Aggregation)',
  unpacking: 'Unpacking (Aggregation)',
};

const ACTOR_LABEL = {
  producer: 'Producer',
  carrier: 'Carrier',
  warehouse: 'Warehouse',
  receiver: 'Receiver',
  admin: 'Admin',
};

const CUSTODY_MODE_LABEL = {
  device_transferred: 'Device Travels',
  device_retained_sender: 'Zone Continuation',
  zone_only: 'Zone Only',
  gateway_handoff: 'Gateway Handoff',
};

function getCustodyModeLabel(handoff) {
  if (handoff?.custody_mode && CUSTODY_MODE_LABEL[handoff.custody_mode]) {
    return CUSTODY_MODE_LABEL[handoff.custody_mode];
  }
  return handoff?.sensor_retained ? 'Device Travels' : 'Zone Continuation';
}

function formatTemp(value) {
  if (value == null || isNaN(value)) return '--.-°C';
  return `${Number(value).toFixed(1)}°C`;
}

const TIMELINE_DOT_COLOR = {
  commissioning: '#0f62fe',
  packing: '#0072c3',
  unpacking: '#00539a',
  shipping: '#6929c4',
  receiving: '#24a148',
};

const DISPOSITION_LABEL = {
  in_progress: 'In progress',
  in_transit: 'In transit',
  accepted: 'Accepted',
  in_quarantine: 'In quarantine',
};

function getTimelineEventTitle(evt) {
  if (evt.type === 'AggregationEvent') {
    return evt.action === 'ADD' ? 'Aggregated (Packed)' : 'Unpacked (Aggregation)';
  }
  if (evt.bizStep === 'commissioning') return 'Created / Commissioned';
  if (evt.bizStep === 'shipping') {
    const actor = evt.actorType ? ACTOR_LABEL[evt.actorType] || evt.actorType : null;
    return actor ? `Shipped · ${actor}` : 'Shipped';
  }
  if (evt.bizStep === 'receiving') {
    if (evt.disposition === 'accepted') return 'Delivered';
    if (evt.disposition === 'in_quarantine') return 'Quarantined';
    const actor = evt.actorType ? ACTOR_LABEL[evt.actorType] || evt.actorType : null;
    return actor ? `Received · ${actor}` : 'Received';
  }
  return BIZSTEP_LABEL[evt.bizStep] || evt.bizStep;
}

function getTimelineEventSubtitle(evt) {
  if (evt.type === 'AggregationEvent') {
    const childCount = evt.childEPCs?.length || 0;
    return evt.action === 'ADD'
      ? `Parent + ${childCount} child EPC${childCount === 1 ? '' : 's'}`
      : `Removed ${childCount} child EPC${childCount === 1 ? '' : 's'}`;
  }
  if (evt.actorType && evt.custodyMode) {
    return `${CUSTODY_MODE_LABEL[evt.custodyMode] || evt.custodyMode}`;
  }
  if (evt.bizStep === 'commissioning' && evt.ilmd?.productName) {
    return evt.ilmd.productName;
  }
  return evt.type === 'ObjectEvent' ? 'ObjectEvent' : evt.type;
}

const ShipmentDetailPage = () => {
  const { id: shipmentId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { zones } = useZones();
  const { sensors } = useSensorsOptimized();
  const { gateways } = useGatewaysOptimized();

  const [shipment, setShipment] = useState(null);
  const [handoffs, setHandoffs] = useState([]);
  const [telemetry, setTelemetry] = useState([]);
  const [gatewayBinding, setGatewayBinding] = useState(null);
  const [aggregations, setAggregations] = useState([]);
  const [relatedShipmentsById, setRelatedShipmentsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('telemetry');
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);

  // Aggregation (EPCIS pack/unpack) — additive to existing flow
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [packCandidates, setPackCandidates] = useState([]);
  const [packSelectedIds, setPackSelectedIds] = useState([]);
  const [packLoading, setPackLoading] = useState(false);
  const [isPackSubmitting, setIsPackSubmitting] = useState(false);
  const [isUnpackSubmitting, setIsUnpackSubmitting] = useState(false);

  // Status transition modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('in_transit');
  const [qaNotes, setQaNotes] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Handoff modal — QR-first flow
  const [handoffModalOpen, setHandoffModalOpen] = useState(false);
  const [handoffScannerOpen, setHandoffScannerOpen] = useState(false);
  const [handoffScannedEpc, setHandoffScannedEpc] = useState('');
  const [handoffScannedShipment, setHandoffScannedShipment] = useState(null);
  const [handoffScanError, setHandoffScanError] = useState(null);
  const [handoffIsLookingUp, setHandoffIsLookingUp] = useState(false);
  const [handoffRetainSensor, setHandoffRetainSensor] = useState(true);
  const [handoffSensorId, setHandoffSensorId] = useState(null);
  const [handoffZoneId, setHandoffZoneId] = useState(null);
  const [handoffActorType, setHandoffActorType] = useState('carrier');
  const [handoffNewStatus, setHandoffNewStatus] = useState('in_transit');
  const [isHandoffSubmitting, setIsHandoffSubmitting] = useState(false);

  // Handoff authorization + next-recipient designation
  const [handoffAuthChecking, setHandoffAuthChecking] = useState(false);
  const [handoffAuthInfo, setHandoffAuthInfo] = useState(null);
  const [handoffNextRecipientEmail, setHandoffNextRecipientEmail] = useState('');
  const [handoffNextRecipientRole, setHandoffNextRecipientRole] = useState('receiver');
  const [handoffGatewayId, setHandoffGatewayId] = useState(null);
  const [noZoneModalOpen, setNoZoneModalOpen] = useState(false);

  // Edit product metadata modal
  const [editMetaModalOpen, setEditMetaModalOpen] = useState(false);
  const [editProductName, setEditProductName] = useState('');
  const [editLotNumber, setEditLotNumber] = useState('');
  const [editGtin, setEditGtin] = useState('');
  const [editSscc, setEditSscc] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [editQuantityUnit, setEditQuantityUnit] = useState('units');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [isMetaSaving, setIsMetaSaving] = useState(false);
  const [gtinAutoCorrected, setGtinAutoCorrected] = useState(false);
  const [ssccAutoCorrected, setSsccAutoCorrected] = useState(false);

  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const bgTertiary = isDark ? '#333333' : '#e8e8e8';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const zoneById = useMemo(() => {
    const map = {};
    (zones || []).forEach((z) => { map[z.id] = z; });
    return map;
  }, [zones]);

  const sensorById = useMemo(() => {
    const map = {};
    (sensors || []).forEach((s) => { map[s.id] = s; });
    return map;
  }, [sensors]);

  const fetchAllData = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    setError(null);
    try {
      const [ship, handoffList, binding, gatewayBindings, aggRows] = await Promise.all([
        getShipment(shipmentId),
        listHandoffs(shipmentId),
        getActiveShipmentGatewayBinding(shipmentId).catch(() => null),
        listShipmentGatewayBindings(shipmentId).catch(() => []),
        listShipmentAggregations(shipmentId).catch(() => []),
      ]);
      const telemetryData = await fetchShipmentTelemetry(ship, handoffList, gatewayBindings);
      const relatedIds = [
        ...new Set(
          (aggRows || []).flatMap((row) => [row.parent_shipment_id, row.child_shipment_id])
        ),
      ];
      const related = relatedIds.length > 0 ? await getShipmentsByIds(relatedIds).catch(() => []) : [];
      const relatedMap = {};
      related.forEach((s) => { relatedMap[s.id] = s; });
      relatedMap[ship.id] = ship;

      setShipment(ship);
      setHandoffs(handoffList);
      setTelemetry(telemetryData);
      setGatewayBinding(binding);
      setAggregations(aggRows || []);
      setRelatedShipmentsById(relatedMap);
    } catch (err) {
      console.error('Error fetching shipment detail:', err);
      setError(err.message || 'Failed to load shipment');
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Build EPCIS event stream from handoffs + telemetry
  const epcisEvents = useMemo(() => {
    if (!shipment) return [];

    const events = [];

    // Commissioning event
    const meta = shipment.metadata || {};
    events.push({
      type: 'ObjectEvent',
      action: 'ADD',
      bizStep: 'commissioning',
      disposition: 'in_progress',
      eventTime: shipment.started_at,
      readPoint: zoneById[shipment.zone_id]
        ? `geo:${zoneById[shipment.zone_id].latitude},${zoneById[shipment.zone_id].longitude}`
        : 'unknown',
      epcList: [shipment.epc_uri],
      sensorId: shipment.sensor_id,
      sensorName: sensorById[shipment.sensor_id]?.name || shipment.sensor_id,
      ilmd: Object.keys(meta).length > 0 ? {
        productName: meta.product_name,
        lotNumber: meta.lot_number,
        gtin: meta.gtin,
        sscc: meta.sscc,
        quantity: meta.quantity,
        quantityUnit: meta.quantity_unit,
        expiryDate: meta.expiry_date,
      } : undefined,
    });

    // Handoff events
    handoffs.forEach((h) => {
      const isReceiving = h.actor_type === 'warehouse' || h.actor_type === 'receiver';
      events.push({
        type: 'ObjectEvent',
        action: 'OBSERVE',
        bizStep: isReceiving ? 'receiving' : 'shipping',
        disposition: 'in_transit',
        eventTime: h.telemetry_cutover_at || h.handoff_at,
        readPoint: zoneById[h.to_zone_id]
          ? `geo:${zoneById[h.to_zone_id].latitude},${zoneById[h.to_zone_id].longitude}`
          : 'unknown',
        epcList: [shipment.epc_uri],
        sensorId: h.to_sensor_id,
        sensorName: sensorById[h.to_sensor_id]?.name || h.to_sensor_id,
        sensorRetained: h.sensor_retained,
        custodyMode: h.custody_mode,
        fromGatewayId: h.from_gateway_id,
        toGatewayId: h.to_gateway_id,
        telemetryCutoverAt: h.telemetry_cutover_at,
        actorType: h.actor_type,
      });
    });

    // AggregationEvents (packing ADD / unpacking DELETE)
    const defaultAggReadPoint = zoneById[shipment.zone_id]
      ? `geo:${zoneById[shipment.zone_id].latitude},${zoneById[shipment.zone_id].longitude}`
      : 'unknown';
    const epcFor = (id) => relatedShipmentsById[id]?.epc_uri || (id === shipment.id ? shipment.epc_uri : null);

    const addGroups = new Map();
    aggregations.forEach((row) => {
      const key = `${row.aggregation_group_id}|${row.packed_at}`;
      if (!addGroups.has(key)) {
        addGroups.set(key, { parentId: row.parent_shipment_id, packedAt: row.packed_at, childIds: [] });
      }
      addGroups.get(key).childIds.push(row.child_shipment_id);
    });
    addGroups.forEach((group) => {
      const parentID = epcFor(group.parentId);
      const childEPCs = group.childIds.map(epcFor).filter(Boolean);
      if (!parentID || childEPCs.length === 0) return;
      events.push({
        type: 'AggregationEvent',
        action: 'ADD',
        bizStep: 'packing',
        disposition: 'in_progress',
        eventTime: group.packedAt,
        readPoint: defaultAggReadPoint,
        parentID,
        childEPCs,
        epcList: [parentID, ...childEPCs],
      });
    });

    const deleteGroups = new Map();
    aggregations.forEach((row) => {
      if (!row.unpacked_at) return;
      const key = `${row.aggregation_group_id}|${row.unpacked_at}`;
      if (!deleteGroups.has(key)) {
        deleteGroups.set(key, { parentId: row.parent_shipment_id, unpackedAt: row.unpacked_at, childIds: [] });
      }
      deleteGroups.get(key).childIds.push(row.child_shipment_id);
    });
    deleteGroups.forEach((group) => {
      const parentID = epcFor(group.parentId);
      const childEPCs = group.childIds.map(epcFor).filter(Boolean);
      if (!parentID || childEPCs.length === 0) return;
      events.push({
        type: 'AggregationEvent',
        action: 'DELETE',
        bizStep: 'unpacking',
        disposition: 'in_progress',
        eventTime: group.unpackedAt,
        readPoint: defaultAggReadPoint,
        parentID,
        childEPCs,
        epcList: [parentID, ...childEPCs],
      });
    });

    // In-transit telemetry samples (batched — every 10th point to avoid flooding)
    const inTransitSamples = telemetry
      .filter((_, idx) => idx % 10 === 0)
      .map((t) => ({
        type: 'ObjectEvent',
        action: 'OBSERVE',
        bizStep: 'in_transit',
        disposition: 'in_transit',
        eventTime: t.recorded_at,
        readPoint: t.latitude != null ? `geo:${t.latitude},${t.longitude}` : 'unknown',
        epcList: [shipment.epc_uri],
        sensorId: shipment.sensor_id,
        sensorName: sensorById[shipment.sensor_id]?.name || shipment.sensor_id,
        temperature: t.temperature,
        humidity: t.humidity,
      }));
    events.push(...inTransitSamples);

    // Final receiving event if delivered
    if (shipment.status === 'delivered' || shipment.status === 'quarantined') {
      events.push({
        type: 'ObjectEvent',
        action: 'OBSERVE',
        bizStep: 'receiving',
        disposition: shipment.status === 'quarantined' ? 'in_quarantine' : 'accepted',
        eventTime: shipment.completed_at,
        readPoint: zoneById[shipment.zone_id]
          ? `geo:${zoneById[shipment.zone_id].latitude},${zoneById[shipment.zone_id].longitude}`
          : 'unknown',
        epcList: [shipment.epc_uri],
        sensorId: shipment.sensor_id,
        sensorName: sensorById[shipment.sensor_id]?.name || shipment.sensor_id,
      });
    }

    return events.sort((a, b) => new Date(a.eventTime) - new Date(b.eventTime));
  }, [shipment, handoffs, telemetry, zoneById, sensorById, aggregations, relatedShipmentsById]);

  // Lifecycle-only events for the left-hand timeline (no in-transit telemetry samples)
  const epcisTimelineEvents = useMemo(
    () => epcisEvents.filter((evt) => evt.bizStep !== 'in_transit'),
    [epcisEvents]
  );

  // Generate downloadable EPCIS JSON-LD document
  const epcisJsonLd = useMemo(() => {
    if (!shipment) return null;

    const context = 'https://ref.gs1.org/standards/epcis/2.0.0/epcis-context.jsonld';
    const meta = shipment.metadata || {};

    // Build quantityList from product metadata if GTIN exists
    const quantityList = [];
    if (meta.gtin) {
      const epcClass = formatLgtinUrn(meta.gtin, meta.lot_number);
      if (epcClass) {
        quantityList.push({
          epcClass,
          quantity: meta.quantity != null ? meta.quantity : 1,
          uom: toGs1Uom(meta.quantity_unit),
        });
      }
    }

    // Get first telemetry reading for commissioning event temperature value
    const firstReading = telemetry.length > 0 ? telemetry[0] : null;

    const eventList = epcisEvents.map((evt) => {
      if (evt.type === 'AggregationEvent') {
        return {
          type: 'AggregationEvent',
          eventTime: evt.eventTime,
          eventTimeZoneOffset: '+00:00',
          parentID: evt.parentID,
          childEPCs: evt.childEPCs,
          action: evt.action,
          bizStep: `urn:epcglobal:cbv:bizstep:${evt.bizStep}`,
          disposition: `urn:epcglobal:cbv:disp:${evt.disposition}`,
          readPoint: { id: evt.readPoint },
        };
      }

      const eventObj = {
        type: evt.type,
        eventTime: evt.eventTime,
        eventTimeZoneOffset: '+00:00',
        action: evt.action,
        bizStep: `urn:epcglobal:cbv:bizstep:${evt.bizStep}`,
        disposition: `urn:epcglobal:cbv:disp:${evt.disposition}`,
        readPoint: { id: evt.readPoint },
        epcList: evt.epcList,
      };

      // Add quantityList to commissioning event if product metadata exists
      if (evt.bizStep === 'commissioning' && quantityList.length > 0) {
        eventObj.quantityList = quantityList;
      }

      // Build sensorReport — only include entries with actual values
      const sensorReport = [];

      // For commissioning event, use first telemetry reading if available
      if (evt.bizStep === 'commissioning' && firstReading) {
        if (firstReading.temperature != null) {
          sensorReport.push({ type: 'gs1:Temperature', value: Number(firstReading.temperature), uom: 'CEL' });
        }
        if (firstReading.humidity != null) {
          sensorReport.push({ type: 'gs1:RelativeHumidity', value: Number(firstReading.humidity), uom: 'P1' });
        }
      } else if (evt.temperature != null) {
        sensorReport.push({ type: 'gs1:Temperature', value: Number(evt.temperature), uom: 'CEL' });
        if (evt.humidity != null) {
          sensorReport.push({ type: 'gs1:RelativeHumidity', value: Number(evt.humidity), uom: 'P1' });
        }
      }

      // Always include sensorElementList with deviceID, even if no readings
      eventObj.sensorElementList = [{
        sensorMetadata: {
          time: evt.eventTime,
          deviceID: `urn:chillverify:sensor:${evt.sensorId}`,
        },
        sensorReport,
      }];

      return eventObj;
    });

    return {
      '@context': context,
      type: 'EPCISDocument',
      schemaVersion: '2.0',
      creationDate: new Date().toISOString(),
      epcisBody: {
        eventList,
      },
    };
  }, [shipment, epcisEvents, telemetry]);

  const handleDownloadJson = () => {
    if (!epcisJsonLd) return;
    const blob = new Blob([JSON.stringify(epcisJsonLd, null, 2)], { type: 'application/ld+json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `epcis-shipment-${shipmentId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyUrl = () => {
    const apiUrl = `${window.location.origin}/netlify/functions/epcis-events?shipmentId=${shipmentId}`;
    navigator.clipboard.writeText(apiUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleStatusTransition = async () => {
    setIsTransitioning(true);
    try {
      const extra = {};
      if (newStatus === 'delivered' && qaNotes.trim()) {
        extra.qa_notes = qaNotes.trim();
      }
      if (newStatus === 'quarantined' && qaNotes.trim()) {
        extra.qa_notes = qaNotes.trim();
      }
      await updateShipmentStatus(shipmentId, newStatus, extra);
      setNotification({ kind: 'success', title: 'Status updated', subtitle: `Shipment marked as ${STATUS_LABEL[newStatus]}` });
      setStatusModalOpen(false);
      setQaNotes('');
      fetchAllData();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Update failed', subtitle: err.message });
    } finally {
      setIsTransitioning(false);
    }
  };

  // QR-first handoff: scan → lookup → confirm
  const handleHandoffScanSuccess = async (decodedText) => {
    setHandoffScannerOpen(false);
    setHandoffScannedEpc(decodedText);
    setHandoffScanError(null);
    setHandoffIsLookingUp(true);
    setHandoffScannedShipment(null);
    setHandoffAuthInfo(null);
    try {
      const found = await getShipmentByEpc(decodedText.trim());
      if (!found) {
        setHandoffScanError(`No shipment found for EPC: ${decodedText}`);
      } else {
        setHandoffScannedShipment(found);
        setHandoffSensorId(found.sensor_id || null);
        // Check authorization for this shipment
        setHandoffAuthChecking(true);
        try {
          const authResult = await checkHandoffAuthorization(found.id);
          setHandoffAuthInfo(authResult);
        } catch (authErr) {
 setHandoffAuthInfo({ authorized: false, error: authErr.message });
        } finally {
          setHandoffAuthChecking(false);
        }
      }
    } catch (err) {
      setHandoffScanError(err.message || 'Failed to look up shipment');
    } finally {
      setHandoffIsLookingUp(false);
    }
  };

  const resetHandoffFlow = () => {
    setHandoffScannedEpc('');
    setHandoffScannedShipment(null);
    setHandoffScanError(null);
    setHandoffIsLookingUp(false);
    setHandoffRetainSensor(true);
    setHandoffSensorId(null);
    setHandoffZoneId(null);
    setHandoffActorType('carrier');
    setHandoffNewStatus('in_transit');
    setHandoffAuthInfo(null);
    setHandoffAuthChecking(false);
    setHandoffNextRecipientEmail('');
    setHandoffNextRecipientRole('receiver');
    setHandoffGatewayId(null);
  };

  const handleHandoffSubmit = async () => {
    const targetShipment = handoffScannedShipment || shipment;
    if (!targetShipment) {
      setNotification({ kind: 'error', title: 'No shipment', subtitle: 'Scan a QR code first to identify the shipment.' });
      return;
    }

    if (!handoffRetainSensor) {
      if (!zones?.length) {
        setNoZoneModalOpen(true);
        return;
      }
      if (!handoffZoneId) {
        setNotification({
          kind: 'error',
          title: 'Zone required',
          subtitle: 'Select your truck/warehouse zone so telemetry can continue after handoff.',
        });
        return;
      }
    }

    setIsHandoffSubmitting(true);
    try {
      const result = await confirmHandoff({
        shipmentId: targetShipment.id,
        toSensorId: handoffRetainSensor
          ? (handoffSensorId || targetShipment.sensor_id)
          : null,
        toZoneId: handoffZoneId,
        sensorRetained: handoffRetainSensor,
        actorType: handoffActorType,
        newStatus: handoffNewStatus,
        nextRecipientEmail: handoffNextRecipientEmail.trim() || undefined,
        nextRecipientRole: handoffNextRecipientRole,
        toGatewayId: handoffGatewayId,
      });
      const subtitle = result.nextAuthorization
        ? 'Shipment handoff recorded. Next recipient has been notified.'
        : 'Shipment handoff recorded and EPCIS payload updated.';
      setNotification({ kind: 'success', title: 'Custody transferred', subtitle });
      setHandoffModalOpen(false);
      resetHandoffFlow();
      // If we're on the detail page for this shipment, refresh
      if (targetShipment.id === shipmentId) {
        fetchAllData();
      } else {
        // Navigated from scan — go to the shipment detail page
        navigate(`/dashboard/shipments/${targetShipment.id}`);
      }
    } catch (err) {
      setNotification({ kind: 'error', title: 'Handoff failed', subtitle: err.message });
    } finally {
      setIsHandoffSubmitting(false);
    }
  };

  const openEditMetaModal = () => {
    const meta = shipment?.metadata || {};
    setEditProductName(meta.product_name || '');
    setEditLotNumber(meta.lot_number || '');
    setEditGtin(meta.gtin || '');
    setEditSscc(meta.sscc || '');
    setEditQuantity(meta.quantity != null ? String(meta.quantity) : '');
    setEditQuantityUnit(meta.quantity_unit || 'units');
    setEditExpiryDate(meta.expiry_date || '');
    setGtinAutoCorrected(false);
    setSsccAutoCorrected(false);
    setEditMetaModalOpen(true);
  };

  const handleGtinChange = (value) => {
    const { value: nextValue, corrected } = applyGtinInputNormalization(value);
    setEditGtin(nextValue);
    setGtinAutoCorrected(corrected);
  };

  const handleSsccChange = (value) => {
    const { value: nextValue, corrected } = applySsccInputNormalization(value);
    setEditSscc(nextValue);
    setSsccAutoCorrected(corrected);
  };

  const handleSaveMetadata = async () => {
    setIsMetaSaving(true);
    try {
      const metadata = {};
      if (editProductName.trim()) metadata.product_name = editProductName.trim();
      if (editLotNumber.trim()) metadata.lot_number = editLotNumber.trim();
      if (editGtin.trim()) {
        metadata.gtin = normalizeGtin14(editGtin.trim()) || editGtin.trim();
      }
      if (editSscc.trim()) {
        metadata.sscc = normalizeSscc18(editSscc.trim()) || editSscc.trim();
      }
      if (editQuantity) metadata.quantity = Number(editQuantity);
      if (editQuantityUnit) metadata.quantity_unit = editQuantityUnit;
      if (editExpiryDate) metadata.expiry_date = editExpiryDate;

      const updated = await updateShipmentMetadata(shipment.id, Object.keys(metadata).length > 0 ? metadata : null);
      setShipment(updated);
      setEditMetaModalOpen(false);
      setNotification({ kind: 'success', title: 'Product details saved', subtitle: 'Shipment metadata updated.' });
    } catch (err) {
      setNotification({ kind: 'error', title: 'Save failed', subtitle: err.message });
    } finally {
      setIsMetaSaving(false);
    }
  };

  const activeChildAggs = useMemo(
    () => aggregations.filter((a) => a.parent_shipment_id === shipmentId && !a.unpacked_at),
    [aggregations, shipmentId]
  );
  const activeParentAgg = useMemo(
    () => aggregations.find((a) => a.child_shipment_id === shipmentId && !a.unpacked_at) || null,
    [aggregations, shipmentId]
  );

  const openPackModal = async () => {
    setPackModalOpen(true);
    setPackSelectedIds([]);
    setPackLoading(true);
    try {
      const [all, packedChildIds] = await Promise.all([
        listShipments(),
        listActivePackedChildIds().catch(() => []),
      ]);
      const activeChildIds = new Set(packedChildIds);
      const candidates = (all || []).filter((s) => (
        s.id !== shipmentId
        && s.epc_uri
        && (s.status === 'commissioned' || s.status === 'in_transit')
        && !activeChildIds.has(s.id)
      ));
      setPackCandidates(candidates);
    } catch (err) {
      setNotification({ kind: 'error', title: 'Could not load shipments', subtitle: err.message });
      setPackCandidates([]);
    } finally {
      setPackLoading(false);
    }
  };

  const togglePackCandidate = (id) => {
    setPackSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const handlePackSubmit = async () => {
    if (packSelectedIds.length === 0) return;
    setIsPackSubmitting(true);
    try {
      await packShipmentsIntoParent(shipmentId, packSelectedIds);
      setPackModalOpen(false);
      setNotification({
        kind: 'success',
        title: 'Shipments packed',
        subtitle: `AggregationEvent ADD recorded for ${packSelectedIds.length} child shipment(s).`,
      });
      await fetchAllData();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Pack failed', subtitle: err.message });
    } finally {
      setIsPackSubmitting(false);
    }
  };

  const handleUnpack = async (childIds = null) => {
    setIsUnpackSubmitting(true);
    try {
      await unpackShipmentsFromParent(shipmentId, childIds);
      setNotification({
        kind: 'success',
        title: 'Shipments unpacked',
        subtitle: 'AggregationEvent DELETE recorded.',
      });
      await fetchAllData();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Unpack failed', subtitle: err.message });
    } finally {
      setIsUnpackSubmitting(false);
    }
  };

  const shipmentLabel = (s) => {
    if (!s) return '—';
    const name = s.metadata?.product_name;
    const shortEpc = s.epc_uri ? s.epc_uri.replace(/^urn:epc:id:sscc:/, '') : s.id?.slice(0, 8);
    return name ? `${name} (${shortEpc})` : (shortEpc || s.id);
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: textSecondary }}>Loading shipment...</p>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }}>
        <Button kind="ghost" renderIcon={ArrowLeft} onClick={() => navigate('/dashboard/shipments')}>Back to Shipments</Button>
        <p style={{ color: '#da1e28', marginTop: '16px' }}>{error || 'Shipment not found'}</p>
      </div>
    );
  }

  const currentZone = zoneById[shipment.zone_id];
  const currentSensor = sensorById[shipment.sensor_id];
  const isActive = shipment.status === 'commissioned' || shipment.status === 'in_transit';

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="shipment-detail-page">
      {/* Notification */}
      {notification && (
        <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 9999, maxWidth: '400px' }}>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            lowContrast
          />
        </div>
      )}

      {/* Status Transition Modal */}
      <Modal
        open={statusModalOpen}
        modalHeading="Update Shipment Status"
        primaryButtonText={isTransitioning ? 'Updating...' : 'Confirm Status Change'}
        primaryButtonDisabled={isTransitioning}
        secondaryButtonText="Cancel"
        onRequestClose={() => setStatusModalOpen(false)}
        onRequestSubmit={handleStatusTransition}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <RadioButtonGroup
            legendText="New Status"
            name="new-status"
            valueSelected={newStatus}
            onChange={(value) => setNewStatus(value)}
          >
            <RadioButton labelText="In Transit" value="in_transit" id="ns-transit" />
            <RadioButton labelText="Delivered" value="delivered" id="ns-delivered" />
            <RadioButton labelText="Quarantined" value="quarantined" id="ns-quarantined" />
          </RadioButtonGroup>
          {(newStatus === 'delivered' || newStatus === 'quarantined') && (
            <TextArea
              id="qa-notes"
              labelText="QA Notes / Excursion Report (optional)"
              placeholder="Document any temperature excursion findings..."
              value={qaNotes}
              onChange={(e) => setQaNotes(e.target.value)}
              rows={3}
            />
          )}
        </div>
      </Modal>

      {/* Handoff Modal */}
      <Modal
        open={handoffModalOpen}
        modalHeading="Custody Transfer / Handoff"
        primaryButtonText={isHandoffSubmitting ? 'Transferring...' : (handoffScannedShipment ? 'Confirm Handoff' : 'Scan QR First')}
        primaryButtonDisabled={isHandoffSubmitting || !handoffScannedShipment || (handoffAuthInfo && !handoffAuthInfo.authorized) || handoffAuthChecking}
        secondaryButtonText="Cancel"
        onRequestClose={() => { setHandoffModalOpen(false); resetHandoffFlow(); }}
        onRequestSubmit={handleHandoffSubmit}
        size="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Step 1: Scan QR Code */}
          {!handoffScannedShipment && !handoffIsLookingUp && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <QrCode size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
              <h4 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                Scan Shipment QR Code
              </h4>
              <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
                Scan the QR code on the pallet label to identify and receive this shipment.
              </p>
              <Button
                kind="primary"
                renderIcon={Camera}
                onClick={() => setHandoffScannerOpen(true)}
                data-testid="handoff-scan-btn"
              >
                Open Scanner
              </Button>
              {handoffScanError && (
                <div style={{ marginTop: '16px' }}>
                  <InlineNotification
                    kind="error"
                    title="Scan failed"
                    subtitle={handoffScanError}
                    onCloseButtonClick={() => setHandoffScanError(null)}
                    lowContrast
                  />
                </div>
              )}
            </div>
          )}

          {/* Looking up shipment */}
          {handoffIsLookingUp && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                Looking up shipment for EPC: <span style={{ fontFamily: 'monospace' }}>{handoffScannedEpc}</span>
              </p>
              <p style={{ color: textSecondary, fontSize: '0.875rem', marginTop: '8px' }}>Searching...</p>
            </div>
          )}

          {/* Step 2: Shipment found — show details + handoff options */}
          {handoffScannedShipment && (
            <>
              {/* Scanned shipment details */}
              <Tile style={{ backgroundColor: bgTertiary, border: `1px solid ${borderColor}`, padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Shipment Identified</p>
                    <p style={{ color: textPrimary, fontSize: '0.8125rem', fontFamily: 'monospace', margin: '4px 0 0 0', wordBreak: 'break-all' }}>
                      {handoffScannedShipment.epc_uri}
                    </p>
                  </div>
                  <Tag type={STATUS_TAG_TYPE[handoffScannedShipment.status] || 'gray'} size="sm">
                    {STATUS_LABEL[handoffScannedShipment.status] || handoffScannedShipment.status}
                  </Tag>
                </div>
                {handoffScannedShipment.metadata?.product_name && (
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Product</p>
                    <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0', fontWeight: 500 }}>
                      {handoffScannedShipment.metadata.product_name}
                    </p>
                    {handoffScannedShipment.metadata.lot_number && (
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '2px 0 0 0', fontFamily: 'monospace' }}>
                        Lot: {handoffScannedShipment.metadata.lot_number}
                      </p>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Current Zone</p>
                    <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>
                      {zoneById[handoffScannedShipment.zone_id]?.name || '—'}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Sensor</p>
                    <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>
                      {sensorById[handoffScannedShipment.sensor_id]?.display_name || sensorById[handoffScannedShipment.sensor_id]?.name || '—'}
                    </p>
                  </div>
                </div>
              </Tile>

              {/* Authorization gate */}
              {handoffAuthChecking && (
                <InlineNotification
                  kind="info"
                  title="Checking authorization..."
                  subtitle="Verifying your authorization to receive this shipment."
                  lowContrast
                />
              )}
              {handoffAuthInfo && !handoffAuthInfo.authorized && !handoffAuthChecking && (
                <InlineNotification
                  kind="error"
                  title="Not authorized"
                  subtitle={
                    handoffAuthInfo.pendingAuthorization
                      ? `This shipment is authorized for a different recipient. You're signed in as ${handoffAuthInfo.currentUserEmail || 'an unrecognized user'} — sign in with the designated recipient's email to receive it.`
                      : 'No authorized recipient has been designated for this shipment yet, or you do not have permission to view it.'
                  }
                  lowContrast
                />
              )}
              {handoffAuthInfo && handoffAuthInfo.authorized && !handoffAuthChecking && (
                <InlineNotification
                  kind="success"
                  title="Authorized"
                  subtitle="You are authorized to receive this shipment."
                  lowContrast
                />
              )}

              {/* Sensor custody */}
              <RadioButtonGroup
                legendText="Sensor Custody"
                name="handoff-sensor"
                valueSelected={handoffRetainSensor ? 'retain' : 'replace'}
                onChange={(value) => setHandoffRetainSensor(value === 'retain')}
              >
                <RadioButton
                  labelText="Device travels with pallet (sensor stays on shipment)"
                  value="retain"
                  id="ho-retain"
                />
                <RadioButton
                  labelText="Sender keeps device — continue via my zone after scan"
                  value="replace"
                  id="ho-replace"
                />
              </RadioButtonGroup>

              {!handoffRetainSensor && (
                <InlineNotification
                  kind="info"
                  title="Zone-based continuation"
                  subtitle="Sender telemetry is kept up to this scan. After you accept, readings from your selected zone (and optional gateway) append to the same shipment record."
                  lowContrast
                />
              )}

              {handoffRetainSensor && (handoffScannedShipment || shipment) && (
                <Dropdown
                  id="handoff-sensor-select"
                  titleText="Monitoring Device"
                  label="Keep pallet sensor or swap to your device"
                  items={[
                    {
                      id: (handoffScannedShipment || shipment).sensor_id,
                      label: `${sensorById[(handoffScannedShipment || shipment).sensor_id]?.name || 'Pallet sensor'} (current on shipment)`,
                    },
                    ...(sensors || [])
                      .filter((s) => s.id !== (handoffScannedShipment || shipment).sensor_id)
                      .map((s) => ({
                        id: s.id,
                        label: s.name || s.mac_address || s.id,
                      })),
                  ]}
                  itemToString={(item) => (item ? item.label : '')}
                  selectedItem={(() => {
                    const target = handoffScannedShipment || shipment;
                    const selectedId = handoffSensorId || target?.sensor_id;
                    const current = {
                      id: target?.sensor_id,
                      label: `${sensorById[target?.sensor_id]?.name || 'Pallet sensor'} (current on shipment)`,
                    };
                    const swapped = (sensors || []).find((s) => s.id === selectedId);
                    if (swapped && swapped.id !== target?.sensor_id) {
                      return {
                        id: swapped.id,
                        label: swapped.name || swapped.mac_address || swapped.id,
                      };
                    }
                    return current;
                  })()}
                  onChange={({ selectedItem }) => setHandoffSensorId(selectedItem?.id || null)}
                />
              )}

              {/* Destination zone */}
              <Dropdown
                id="handoff-zone-select"
                titleText={handoffRetainSensor ? 'Destination Zone (optional)' : 'Your Zone (required)'}
                label={handoffRetainSensor ? 'Select destination zone' : 'Select your truck/warehouse zone'}
                items={(zones || []).map((z) => ({ id: z.id, label: z.name }))}
                itemToString={(item) => (item ? item.label : '')}
                onChange={({ selectedItem }) => setHandoffZoneId(selectedItem?.id || null)}
              />

              {/* Optional receiver gateway for MQTT continuity */}
              {(gateways || []).length > 0 && (
                <Dropdown
                  id="handoff-gateway-select"
                  titleText="Gateway (optional)"
                  label="Link your gateway for MQTT telemetry"
                  items={[
                    { id: null, label: 'No gateway — use device/zone only' },
                    ...(gateways || []).map((g) => ({
                      id: g.id,
                      label: g.name || g.mac_address || g.id,
                    })),
                  ]}
                  itemToString={(item) => (item ? item.label : '')}
                  onChange={({ selectedItem }) => setHandoffGatewayId(selectedItem?.id || null)}
                />
              )}

              {/* Actor role */}
              <Dropdown
                id="handoff-actor-type"
                titleText="Your Role"
                label="Select your supply chain role"
                items={[
                  { id: 'producer', label: 'Producer' },
                  { id: 'carrier', label: 'Carrier / Driver' },
                  { id: 'warehouse', label: 'Warehouse / DC' },
                  { id: 'receiver', label: 'Receiver' },
                ]}
                itemToString={(item) => (item ? item.label : '')}
                onChange={({ selectedItem }) => setHandoffActorType(selectedItem?.id || 'carrier')}
                initialSelectedItem={{ id: handoffActorType, label: ACTOR_LABEL[handoffActorType] || 'Carrier' }}
              />

              {/* New status */}
              <RadioButtonGroup
                legendText="New Shipment Status"
                name="handoff-status"
                valueSelected={handoffNewStatus}
                onChange={(value) => setHandoffNewStatus(value)}
              >
                <RadioButton labelText="In Transit" value="in_transit" id="hs-transit" />
                <RadioButton labelText="Delivered" value="delivered" id="hs-delivered" />
                <RadioButton labelText="Quarantined" value="quarantined" id="hs-quarantined" />
              </RadioButtonGroup>

              {/* Next recipient designation (optional) */}
              {handoffAuthInfo && handoffAuthInfo.authorized && !handoffAuthChecking && (
                <>
                  <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '16px', marginTop: '8px' }}>
                    <p style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 600, marginBottom: '4px' }}>
                      Designate Next Recipient (optional)
                    </p>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '12px' }}>
                      Enter the email of the person authorized to receive this shipment in the next leg. They will be notified by email.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 220px' }}>
                        <TextInput
                          id="handoff-next-recipient-email"
                          labelText="Next Recipient Email"
                          placeholder="e.g. driver@carrier.com"
                          value={handoffNextRecipientEmail}
                          onChange={(e) => setHandoffNextRecipientEmail(e.target.value)}
                          type="email"
                        />
                      </div>
                      <div style={{ flex: '1 1 160px' }}>
                        <Dropdown
                          id="handoff-next-recipient-role"
                          titleText="Next Recipient Role"
                          label="Select role"
                          items={[
                            { id: 'carrier', label: 'Carrier / Driver' },
                            { id: 'warehouse', label: 'Warehouse / DC' },
                            { id: 'receiver', label: 'Receiver' },
                          ]}
                          itemToString={(item) => (item ? item.label : '')}
                          onChange={({ selectedItem }) => setHandoffNextRecipientRole(selectedItem?.id || 'receiver')}
                          initialSelectedItem={{ id: handoffNextRecipientRole, label: ACTOR_LABEL[handoffNextRecipientRole] || 'Receiver' }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Rescan button */}
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Camera}
                onClick={() => { resetHandoffFlow(); setHandoffScannerOpen(true); }}
                style={{ alignSelf: 'flex-start' }}
              >
                Scan Different Shipment
              </Button>
            </>
          )}
        </div>
      </Modal>

      {/* Handoff QR Scanner */}
      <QRCodeScanner
        isOpen={handoffScannerOpen}
        onClose={() => setHandoffScannerOpen(false)}
        onScanSuccess={handleHandoffScanSuccess}
      />

      {/* Pack children modal (EPCIS AggregationEvent ADD) */}
      <Modal
        open={packModalOpen}
        modalHeading="Pack Child Shipments"
        primaryButtonText={isPackSubmitting ? 'Packing...' : 'Pack Selected'}
        primaryButtonDisabled={isPackSubmitting || packSelectedIds.length === 0 || packLoading}
        secondaryButtonText="Cancel"
        onRequestClose={() => setPackModalOpen(false)}
        onRequestSubmit={handlePackSubmit}
        size="md"
      >
        <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
          Select existing shipments to aggregate under this parent. Each child keeps its own EPC;
          packing records a standard EPCIS AggregationEvent (action=ADD). Create child shipments
          first if needed — this does not change create or handoff.
        </p>
        {packLoading ? (
          <p style={{ color: textSecondary }}>Loading eligible shipments...</p>
        ) : packCandidates.length === 0 ? (
          <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
            No eligible child shipments. Create commissioned/in-transit shipments with an EPC first.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
            {packCandidates.map((s) => (
              <Checkbox
                key={s.id}
                id={`pack-child-${s.id}`}
                labelText={shipmentLabel(s)}
                checked={packSelectedIds.includes(s.id)}
                onChange={() => togglePackCandidate(s.id)}
              />
            ))}
          </div>
        )}
      </Modal>

      {/* Header — match ShipmentsListPage layout; box-shadow fills nav padding area grey */}
      <div style={{
        backgroundColor: bgSecondary,
        borderBottom: `1px solid ${borderColor}`,
        padding: '24px 0',
        boxShadow: `0 -48px 0 0 ${bgSecondary}`,
      }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button kind="ghost" size="sm" renderIcon={ArrowLeft} onClick={() => navigate('/dashboard/shipments')} />
                <div>
                  <h1 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
                    Shipment Detail
                  </h1>
                  <p style={{ color: textSecondary, fontSize: '0.8125rem', fontFamily: 'monospace', margin: '4px 0 0 0' }}>
                    {shipment.epc_uri || '—'}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Tag type={STATUS_TAG_TYPE[shipment.status] || 'gray'} size="md">
                  {STATUS_LABEL[shipment.status] || shipment.status}
                </Tag>
                {isActive && (
                  <>
                    <Button kind="tertiary" size="sm" onClick={openPackModal} data-testid="pack-btn">
                      Pack Children
                    </Button>
                    <Button kind="secondary" size="sm" renderIcon={QrCode} onClick={() => { resetHandoffFlow(); setHandoffSensorId(shipment?.sensor_id || null); setHandoffModalOpen(true); }} data-testid="handoff-btn">
                      Receive Shipment
                    </Button>
                    <Button kind="primary" size="sm" onClick={() => setStatusModalOpen(true)} data-testid="status-btn">
                      Update Status
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Body */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          {/* Left: Info Panel */}
          <Column lg={6} md={4} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Shipment Info</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>EPC / SSCC</p>
                  <p style={{ color: textPrimary, fontSize: '0.8125rem', fontFamily: 'monospace', margin: '2px 0 0 0', wordBreak: 'break-all' }}>
                    {shipment.epc_uri || '—'}
                  </p>
                </div>
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Current Zone</p>
                  <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>
                    <Location size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {currentZone?.name || '—'}
                  </p>
                  {currentZone && (
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '2px 0 0 0' }}>
                      {currentZone.latitude?.toFixed(5)}, {currentZone.longitude?.toFixed(5)}
                    </p>
                  )}
                </div>
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Active Sensor</p>
                  <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>
                    {currentSensor?.display_name || currentSensor?.name || '—'}
                  </p>
                </div>
                {gatewayBinding && (
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Gateway Binding</p>
                    <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>
                      {(gateways || []).find((g) => g.id === gatewayBinding.gateway_id)?.name
                        || (gatewayBinding.gateway_id ? 'Gateway linked' : 'Device / zone only')}
                    </p>
                    <Tag type="blue" size="sm" style={{ marginTop: 4 }}>
                      {gatewayBinding.binding_source || 'device'}
                    </Tag>
                  </div>
                )}
                {shipment.current_custodian_email && (
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Current Custodian</p>
                    <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>
                      {shipment.current_custodian_email}
                    </p>
                  </div>
                )}
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Started</p>
                  <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>
                    <Time size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {shipment.started_at ? new Date(shipment.started_at).toLocaleString() : '—'}
                  </p>
                </div>
                {shipment.completed_at && (
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Completed</p>
                    <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '2px 0 0 0' }}>
                      <Time size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {new Date(shipment.completed_at).toLocaleString()}
                    </p>
                  </div>
                )}
                {shipment.qa_notes && (
                  <div>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>
                      <WarningFilled size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      QA Notes
                    </p>
                    <p style={{ color: textPrimary, fontSize: '0.8125rem', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                      {shipment.qa_notes}
                    </p>
                  </div>
                )}
              </div>
            </Tile>

            {/* EPCIS Aggregation (parent / children) */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }} data-testid="aggregation-tile">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px', flexWrap: 'wrap' }}>
                <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>Aggregation</h3>
                {isActive && (
                  <Button kind="ghost" size="sm" onClick={openPackModal}>
                    Pack Children
                  </Button>
                )}
              </div>
              <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '0 0 12px 0' }}>
                EPCIS parent/child packing. Handoff and telemetry continue on this shipment as before.
              </p>

              {activeParentAgg && (
                <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: bgTertiary, borderRadius: '4px' }}>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Packed under parent</p>
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/shipments/${activeParentAgg.parent_shipment_id}`)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: '#0f62fe', fontSize: '0.8125rem', fontFamily: 'monospace', textAlign: 'left',
                      wordBreak: 'break-all', marginTop: 4,
                    }}
                  >
                    {relatedShipmentsById[activeParentAgg.parent_shipment_id]?.epc_uri
                      || activeParentAgg.parent_shipment_id}
                  </button>
                  <p style={{ color: textSecondary, fontSize: '0.6875rem', margin: '4px 0 0 0' }}>
                    Unpack from the parent shipment detail page.
                  </p>
                </div>
              )}

              {activeChildAggs.length === 0 ? (
                <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>
                  No active child shipments packed under this parent.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {activeChildAggs.map((row) => {
                    const child = relatedShipmentsById[row.child_shipment_id];
                    return (
                      <div
                        key={row.id}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          gap: '8px', padding: '8px', backgroundColor: bgTertiary, borderRadius: '4px',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/shipments/${row.child_shipment_id}`)}
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: textPrimary, fontSize: '0.8125rem', textAlign: 'left', flex: 1,
                          }}
                        >
                          {shipmentLabel(child)}
                        </button>
                        <Button
                          kind="danger--ghost"
                          size="sm"
                          disabled={isUnpackSubmitting}
                          onClick={() => handleUnpack([row.child_shipment_id])}
                        >
                          Unpack
                        </Button>
                      </div>
                    );
                  })}
                  {activeChildAggs.length > 1 && (
                    <Button
                      kind="secondary"
                      size="sm"
                      disabled={isUnpackSubmitting}
                      onClick={() => handleUnpack(null)}
                    >
                      Unpack All
                    </Button>
                  )}
                </div>
              )}
            </Tile>

            {/* Product Details */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>Product Details</h3>
                <Button kind="ghost" size="sm" renderIcon={Edit} onClick={openEditMetaModal} data-testid="edit-product-btn">
                  {shipment.metadata && Object.keys(shipment.metadata).length > 0 ? 'Edit' : 'Add Details'}
                </Button>
              </div>
              {shipment.metadata && Object.keys(shipment.metadata).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {shipment.metadata.product_name && (
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Product Name</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>{shipment.metadata.product_name}</p>
                    </div>
                  )}
                  {shipment.metadata.lot_number && (
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Batch / Lot Number</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', fontFamily: 'monospace', margin: '2px 0 0 0' }}>{shipment.metadata.lot_number}</p>
                    </div>
                  )}
                  {shipment.metadata.gtin && (
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>GTIN</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', fontFamily: 'monospace', margin: '2px 0 0 0' }}>{shipment.metadata.gtin}</p>
                    </div>
                  )}
                  {shipment.metadata.sscc && (
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>SSCC</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', fontFamily: 'monospace', margin: '2px 0 0 0' }}>{shipment.metadata.sscc}</p>
                    </div>
                  )}
                  {shipment.metadata.quantity != null && (
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Quantity</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>{shipment.metadata.quantity} {shipment.metadata.quantity_unit || ''}</p>
                    </div>
                  )}
                  {shipment.metadata.expiry_date && (
                    <div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Expiry Date</p>
                      <p style={{ color: textPrimary, fontSize: '0.875rem', margin: '2px 0 0 0' }}>{new Date(shipment.metadata.expiry_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                  No product details yet. Click "Add Details" to add product name, lot/batch, GTIN, and more.
                </p>
              )}
            </Tile>

            {/* Barcode & Print Label */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Barcode & Pallet Label</h3>
              <ShipmentLabel shipment={shipment} />
            </Tile>

            {/* Export Actions */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>EPCIS Export</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Button kind="secondary" size="sm" renderIcon={Download} onClick={handleDownloadJson} data-testid="download-epcis">
                  Download EPCIS 2.0 JSON-LD
                </Button>
                <Button kind="ghost" size="sm" renderIcon={copied ? Checkmark : Copy} onClick={handleCopyUrl} data-testid="copy-api-url">
                  {copied ? 'Copied!' : 'Copy REST API Event URL'}
                </Button>
                <Toggle
                  id="toggle-json"
                  labelText="Show JSON payload"
                  toggled={showJson}
                  onToggle={(val) => setShowJson(val)}
                  size="sm"
                />
              </div>
              {showJson && epcisJsonLd && (
                <pre
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: bgTertiary,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '4px',
                    fontSize: '0.6875rem',
                    color: textPrimary,
                    overflow: 'auto',
                    maxHeight: '300px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(epcisJsonLd, null, 2)}
                </pre>
              )}
            </Tile>

            {/* EPCIS lifecycle timeline */}
            <Tile
              style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px' }}
              data-testid="epcis-timeline-tile"
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px', flexWrap: 'wrap' }}>
                <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>EPCIS Timeline</h3>
                <Tag type={STATUS_TAG_TYPE[shipment.status] || 'gray'} size="sm">
                  {STATUS_LABEL[shipment.status] || shipment.status}
                </Tag>
              </div>
              <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '0 0 16px 0' }}>
                Created, aggregation, custody handoffs, and delivery events in order.
              </p>

              {epcisTimelineEvents.length === 0 ? (
                <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>No EPCIS lifecycle events yet.</p>
              ) : (
                <div style={{ position: 'relative' }}>
                  {epcisTimelineEvents.map((evt, idx) => {
                    const isLast = idx === epcisTimelineEvents.length - 1;
                    const isCurrent = isLast;
                    const dotColor = TIMELINE_DOT_COLOR[evt.bizStep] || '#8d8d8d';
                    const title = getTimelineEventTitle(evt);
                    const subtitle = getTimelineEventSubtitle(evt);

                    return (
                      <div
                        key={`${evt.eventTime}-${evt.bizStep}-${evt.action}-${idx}`}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          position: 'relative',
                          paddingBottom: isLast ? 0 : '20px',
                        }}
                      >
                        {/* Connector line */}
                        {!isLast && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '5px',
                              top: '14px',
                              bottom: 0,
                              width: '2px',
                              backgroundColor: borderColor,
                            }}
                          />
                        )}

                        {/* Timeline node */}
                        <div
                          style={{
                            flexShrink: 0,
                            width: '12px',
                            height: '12px',
                            marginTop: '4px',
                            borderRadius: '50%',
                            backgroundColor: dotColor,
                            border: `2px solid ${isCurrent ? dotColor : bgSecondary}`,
                            boxShadow: isCurrent ? `0 0 0 2px ${dotColor}55` : 'none',
                            zIndex: 1,
                          }}
                          aria-hidden
                        />

                        {/* Event content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                            <div>
                              <p style={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600, margin: 0 }}>
                                {title}
                                {evt.type === 'AggregationEvent' && (
                                  <span style={{ color: textSecondary, fontWeight: 400 }}> · {evt.action}</span>
                                )}
                              </p>
                              {subtitle && (
                                <p style={{ color: textSecondary, fontSize: '0.6875rem', margin: '2px 0 0 0' }}>
                                  {subtitle}
                                </p>
                              )}
                            </div>
                            {isCurrent && (
                              <Tag type="blue" size="sm">Latest</Tag>
                            )}
                          </div>
                          <p style={{ color: textSecondary, fontSize: '0.6875rem', margin: '4px 0 0 0' }}>
                            <Time size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            {evt.eventTime ? new Date(evt.eventTime).toLocaleString() : '—'}
                          </p>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                            <Tag
                              type={
                                evt.disposition === 'in_progress' ? 'blue'
                                : evt.disposition === 'in_transit' ? 'purple'
                                : evt.disposition === 'accepted' ? 'green'
                                : evt.disposition === 'in_quarantine' ? 'red'
                                : 'gray'
                              }
                              size="sm"
                            >
                              {DISPOSITION_LABEL[evt.disposition] || evt.disposition}
                            </Tag>
                            <Tag type="gray" size="sm">
                              {evt.type === 'AggregationEvent' ? 'AggregationEvent' : 'ObjectEvent'}
                            </Tag>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Tile>
          </Column>

          {/* Right: Telemetry / Event Stream */}
          <Column lg={10} md={4} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                  {viewMode === 'telemetry' ? 'Raw Telemetry' : 'EPCIS Event Stream'}
                </h3>
                <Toggle
                  id="toggle-view"
                  labelText="EPCIS Events"
                  toggled={viewMode === 'epcis'}
                  onToggle={(val) => setViewMode(val ? 'epcis' : 'telemetry')}
                  size="sm"
                />
              </div>

              {viewMode === 'telemetry' ? (
                <div>
                  {telemetry.length === 0 ? (
                    <p style={{ color: textSecondary, fontSize: '0.875rem' }}>No telemetry data available for this shipment's sensor.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <th style={{ padding: '8px', textAlign: 'left', color: textSecondary }}>Time</th>
                            <th style={{ padding: '8px', textAlign: 'right', color: textSecondary }}>Temp</th>
                            <th style={{ padding: '8px', textAlign: 'right', color: textSecondary }}>Humidity</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: textSecondary }}>GPS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {telemetry.slice(-50).reverse().map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: `1px solid ${borderColor}` }}>
                              <td style={{ padding: '8px', color: textSecondary }}>
                                {new Date(row.recorded_at).toLocaleString()}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', color: '#0f62fe', fontWeight: 600 }}>
                                {formatTemp(row.temperature)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', color: '#6929c4' }}>
                                {row.humidity != null ? `${Number(row.humidity).toFixed(1)}%` : '—'}
                              </td>
                              <td style={{ padding: '8px', color: textSecondary, fontFamily: 'monospace', fontSize: '0.6875rem' }}>
                                {row.latitude != null ? `${Number(row.latitude).toFixed(4)}, ${Number(row.longitude).toFixed(4)}` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {telemetry.length > 50 && (
                        <p style={{ color: textSecondary, fontSize: '0.6875rem', marginTop: '8px', textAlign: 'center' }}>
                          Showing latest 50 of {telemetry.length} readings
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {epcisEvents.length === 0 ? (
                    <p style={{ color: textSecondary, fontSize: '0.875rem' }}>No EPCIS events generated yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {epcisEvents.map((evt, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '12px',
                            backgroundColor: bgTertiary,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '4px',
                            borderLeft: `3px solid ${
                              evt.bizStep === 'commissioning' ? '#0f62fe'
                              : evt.bizStep === 'packing' ? '#0072c3'
                              : evt.bizStep === 'unpacking' ? '#00539a'
                              : evt.bizStep === 'shipping' ? '#6929c4'
                              : evt.bizStep === 'in_transit' ? '#8a3ffc'
                              : evt.bizStep === 'receiving' ? '#24a148'
                              : '#da1e28'
                            }`,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600 }}>
                              {BIZSTEP_LABEL[evt.bizStep] || evt.bizStep}
                              {evt.type === 'AggregationEvent' ? ` · ${evt.action}` : ''}
                            </span>
                            <Tag
                              type={
                                evt.disposition === 'in_progress' ? 'blue'
                                : evt.disposition === 'in_transit' ? 'purple'
                                : evt.disposition === 'accepted' ? 'green'
                                : evt.disposition === 'in_quarantine' ? 'red'
                                : 'gray'
                              }
                              size="sm"
                            >
                              {evt.disposition}
                            </Tag>
                          </div>
                          <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                              {evt.eventTime ? new Date(evt.eventTime).toLocaleString() : '—'}
                            </span>
                            <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                              ReadPoint: {evt.readPoint}
                            </span>
                            {evt.type === 'AggregationEvent' ? (
                              <>
                                <span style={{ color: textSecondary, fontSize: '0.6875rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  Parent: {evt.parentID}
                                </span>
                                <span style={{ color: textSecondary, fontSize: '0.6875rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  Children ({evt.childEPCs?.length || 0}): {(evt.childEPCs || []).join(', ')}
                                </span>
                              </>
                            ) : (
                              <>
                                <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                                  Sensor: {evt.sensorName}
                                </span>
                                {evt.temperature != null && (
                                  <span style={{ color: '#0f62fe', fontSize: '0.6875rem', fontWeight: 600 }}>
                                    {formatTemp(evt.temperature)}
                                    {evt.humidity != null && ` | ${Number(evt.humidity).toFixed(1)}%`}
                                  </span>
                                )}
                                {evt.actorType && (
                                  <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                                    Actor: {ACTOR_LABEL[evt.actorType] || evt.actorType}
                                    {evt.custodyMode && ` | ${CUSTODY_MODE_LABEL[evt.custodyMode] || evt.custodyMode}`}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Tile>

            {/* Custody History */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Custody History</h3>
              {handoffs.length === 0 ? (
                <p style={{ color: textSecondary, fontSize: '0.875rem' }}>No custody transfers recorded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {handoffs.map((h, idx) => {
                    const fromZone = zoneById[h.from_zone_id];
                    const toZone = zoneById[h.to_zone_id];
                    const fromSensor = sensorById[h.from_sensor_id];
                    const toSensor = sensorById[h.to_sensor_id];
                    return (
                      <div
                        key={h.id || idx}
                        style={{
                          padding: '12px',
                          backgroundColor: bgTertiary,
                          border: `1px solid ${borderColor}`,
                          borderRadius: '4px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600 }}>
                            {ACTOR_LABEL[h.actor_type] || h.actor_type || 'Unknown'}
                          </span>
                          <Tag
                            type={
                              h.custody_mode === 'device_retained_sender' ? 'purple'
                              : h.custody_mode === 'gateway_handoff' ? 'blue'
                              : h.custody_mode === 'zone_only' ? 'teal'
                              : 'green'
                            }
                            size="sm"
                          >
                            {getCustodyModeLabel(h)}
                          </Tag>
                        </div>
                        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                            {(h.telemetry_cutover_at || h.handoff_at)
                              ? new Date(h.telemetry_cutover_at || h.handoff_at).toLocaleString()
                              : '—'}
                          </span>
                          <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                            {fromZone?.name || '—'} → {toZone?.name || '—'}
                          </span>
                          <span style={{ color: textSecondary, fontSize: '0.6875rem' }}>
                            Sensor: {fromSensor?.name || '—'} → {toSensor?.name || '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Tile>

            {/* Full Label Preview — the same labels that appear in print view,
                shown directly on screen. Click a label type to view it. */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '4px' }}>
                Label Preview
              </h3>
              <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '16px' }}>
                Click a label type below to view the full label on screen.
              </p>
              <ShipmentLabel shipment={shipment} preview />
            </Tile>
          </Column>
        </Grid>
      </div>

      {/* Edit Product Metadata Modal */}
      <Modal
        open={editMetaModalOpen}
        modalHeading="Edit Product Details"
        primaryButtonText={isMetaSaving ? 'Saving...' : 'Save'}
        primaryButtonDisabled={isMetaSaving}
        secondaryButtonText="Cancel"
        onRequestClose={() => setEditMetaModalOpen(false)}
        onRequestSubmit={handleSaveMetadata}
        size="lg"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ fontSize: '0.75rem', color: textSecondary, marginBottom: '4px' }}>
            Fields are grouped by GS1 Application Identifier (AI) and encoded into separate barcode symbols per GS1 Logistics Label standards.
          </p>
          <TextInput
            id="edit-product-name"
            labelText="Product Name"
            placeholder="e.g. Fresh Salmon Fillets"
            value={editProductName}
            onChange={(e) => setEditProductName(e.target.value)}
            helperText="Human-readable description on label (not encoded in barcode)"
          />

          {/* --- Logistic Unit Identifier — GS1-128 barcode #1 (bottom panel) --- */}
          <div style={{
            padding: '12px 16px',
            border: `1px solid ${borderColor}`,
            borderRadius: '6px',
            backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
          }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
              Logistic Unit — GS1-128 Barcode #1 (bottom panel)
            </p>
            <TextInput
              id="edit-sscc"
              labelText="AI (00) — SSCC (Serial Shipping Container Code)"
              placeholder="e.g. 006141410000000001"
              value={editSscc}
              onChange={(e) => handleSsccChange(e.target.value)}
              helperText="18 digits incl. check digit. Encoded alone in its own GS1-128 symbol. Checksum auto-corrected if invalid."
            />
            {ssccAutoCorrected && (
              <InlineNotification
                kind="info"
                title="SSCC checksum auto-corrected"
                subtitle="Invalid checksum detected and fixed automatically"
                lowContrast
                style={{ marginTop: '8px' }}
              />
            )}
          </div>

          {/* --- Product Content — GS1-128 barcode #2 (middle panel) + GS1 DataMatrix --- */}
          <div style={{
            padding: '12px 16px',
            border: `1px solid ${borderColor}`,
            borderRadius: '6px',
            backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
          }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
              Product Content — GS1-128 Barcode #2 (middle panel) & GS1 DataMatrix
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <TextInput
                id="edit-gtin"
                labelText="AI (01) — GTIN (Global Trade Item Number)"
                placeholder="e.g. 00614141000005"
                value={editGtin}
                onChange={(e) => handleGtinChange(e.target.value)}
                helperText="8–14 digits incl. check digit. Fixed-length AI. Checksum auto-corrected if invalid."
              />
              {gtinAutoCorrected && (
                <InlineNotification
                  kind="info"
                  title="GTIN checksum auto-corrected"
                  subtitle="Invalid checksum detected and fixed automatically"
                  lowContrast
                  style={{ marginTop: '8px' }}
                />
              )}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <TextInput
                    id="edit-lot"
                    labelText="AI (10) — Batch / Lot Number"
                    placeholder="e.g. LOT-2024-A042"
                    value={editLotNumber}
                    onChange={(e) => setEditLotNumber(e.target.value)}
                    helperText="Variable-length AI (max 20 chars)"
                  />
                </div>
                <div style={{ flex: '1 1 150px' }}>
                  <TextInput
                    id="edit-expiry"
                    labelText="AI (17) — Expiry Date (YYMMDD)"
                    type="date"
                    value={editExpiryDate}
                    onChange={(e) => setEditExpiryDate(e.target.value)}
                    helperText="Fixed-length AI, encoded as YYMMDD"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* --- Count — GS1-128 Barcode #3 (separate symbol for AI 37) --- */}
          <div style={{
            padding: '12px 16px',
            border: `1px solid ${borderColor}`,
            borderRadius: '6px',
            backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
          }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
              Count — GS1-128 Barcode #3 (middle panel, separate symbol)
            </p>
            <p style={{ fontSize: '0.6875rem', color: textSecondary, marginBottom: '12px' }}>
              AI (37) Count is encoded in its own separate GS1-128 barcode symbol because AIs (01) and (37) are mutually exclusive within the same symbol per GS1 General Specifications.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 120px' }}>
                <NumberInput
                  id="edit-quantity"
                  label="AI (37) — Count of Trade Items"
                  value={editQuantity}
                  onChange={(e, { value }) => setEditQuantity(value)}
                  min={0}
                  step={1}
                  helperText="Variable-length AI, encoded in its own GS1-128 symbol"
                />
              </div>
              <div style={{ flex: '1 1 150px' }}>
                <Select
                  id="edit-quantity-unit"
                  labelText="Unit"
                  value={editQuantityUnit}
                  onChange={(e) => setEditQuantityUnit(e.target.value)}
                >
                  <SelectItem value="units" text="units" />
                  <SelectItem value="cases" text="cases" />
                  <SelectItem value="pallets" text="pallets" />
                  <SelectItem value="kg" text="kg" />
                  <SelectItem value="boxes" text="boxes" />
                  <SelectItem value="crates" text="crates" />
                </Select>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={noZoneModalOpen}
        modalHeading="Zone Required for Traceability"
        primaryButtonText="Create Zone"
        secondaryButtonText="Close"
        onRequestClose={() => setNoZoneModalOpen(false)}
        onRequestSubmit={() => {
          setNoZoneModalOpen(false);
          setHandoffModalOpen(false);
          navigate('/dashboard/zones');
        }}
        size="sm"
      >
        <p style={{ color: textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}>
          No zone record exists for your account. When the sender keeps the monitoring device,
          telemetry can only continue if you assign this shipment to one of your zones (for example,
          your truck zone) after scan and accept.
        </p>
        <p style={{ color: textSecondary, fontSize: '0.8125rem', marginTop: 12, lineHeight: 1.5 }}>
          Without a zone, the device on this pallet must physically travel with the shipment to
          maintain traceability on the same EPCIS record.
        </p>
      </Modal>
    </div>
  );
};

export default ShipmentDetailPage;
